import React, { useContext, useState, useEffect } from 'react';
import {
  Typography,
  Form,
  Row,
  Col,
  Input,
  Button,
  message,
  Modal,
} from 'antd';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { UserContext } from '../../../contexts';
import './UserProfile.css';

const { Title, Text, Link } = Typography;
const { Password } = Input;

const AccountSettings: React.FC = () => {
  // #region Setting Up Context And State

  const [submitting, setSubmitting] = useState('');
  const { user, setUser } = useContext(UserContext);

  /*
   * Setting up references to the different forms used in the component
   */
  const [emailForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [securityCodeForm] = Form.useForm();

  const history = useHistory();

  // #endregion

  // #region UseEffect And LifeCycle Methods

  useEffect(() => {
    document.title = 'EHE Tracker - Account Settings';
  }, []);

  // #endregion

  // #region Verification Modals

  /*
   * If the current email is not verified, this method will resend a verification
   * email to the new email address.
   */
  const resendCode = () => {
    Auth.verifyCurrentUserAttribute('email');
    message.info('A new verification code has been sent to your email.');
  };

  /*
   * When a user updates their email address, this modal will become visible.
   * This modal consists of a form field that will accept an input of the security
   * code sent to the user's new email.
   *
   * On success, it will notify the user that their email has been updated. However,
   * on any error, it will notify the user that they must update their email again.
   */
  const verifyEmailModal = () =>
    Modal.confirm({
      title: 'Verify Email:',
      content: (
        <Form form={securityCodeForm}>
          <Row className="UserProfile-margin-bottom">
            <Text>
              Please enter the confirmation code sent to your new email address.
            </Text>
          </Row>
          <Form.Item name="code" noStyle>
            <Input placeholder="Enter Security Code..." />
          </Form.Item>
          <Row className="UserProfile-margin-top" justify="end">
            <Link onClick={resendCode}>Resend Code</Link>
          </Row>
        </Form>
      ),
      okText: 'Confirm',
      async onOk() {
        try {
          const code = securityCodeForm.getFieldValue('code');
          await Auth.verifyCurrentUserAttributeSubmit('email', code);
          setUser();
          securityCodeForm.resetFields();
          message.success('Your email has been updated!');
        } catch (e) {
          securityCodeForm.resetFields();
          message.error(
            'Looks like something went wrong! Please update your email again.',
          );
        }
      },
      centered: true,
      maskClosable: false,
    });

  // #endregion

  // #region Form Finish and Button Methods

  /*
   * When the email form passes client-side validation, we change the email
   * attribute of the current authenticated user. Since the cognito pool requires
   * the email to be verified, a verification code is sent to the new email address.
   *
   * Therefore, we show the email verification modal to allow the user to verify
   * their email.
   *
   * If there are errors, it will display the error message.
   */
  const onEmailFinish = async (values: any) => {
    try {
      setSubmitting('EMAIL');
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(currentUser, { email: values.email });
      setSubmitting('');
      verifyEmailModal();
    } catch (e) {
      setSubmitting('');
      message.error(e.message);
    }
  };

  /*
   * When the password form passes client-side validation, we change the
   * password of the current authenticated user. If there are no errors, it
   * will update the user context and display a success message.
   *
   * If there are errors, it will display the error message.
   */
  const onPasswordFinish = async (values: any) => {
    try {
      setSubmitting('PASSWORD');
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        currentUser,
        values.currentPassword,
        values.newPassword,
      );
      setUser();
      setSubmitting('');
      message.success('Your password has been updated!');
    } catch (e) {
      setSubmitting('');
      message.error(e.message);
    }
  };

  const onFinishFailed = () => {
    message.error('Please fix the errors!');
  };

  const onCancelClick = () => {
    history.goBack();
  };

  // #endregion

  return (
    <Row>
      <Col span={8}>
        <Title>Account Settings</Title>
        <Form
          form={emailForm}
          onFinish={onEmailFinish}
          onFinishFailed={onFinishFailed}
          size="large"
        >
          <Title level={3}>Update Email</Title>
          <Title level={4}>Email:</Title>
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
                message: 'Please enter a value for email!',
              },
            ]}
            initialValue={user.attributes.email}
          >
            <Input />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting === 'EMAIL'}
          >
            Save
          </Button>
        </Form>
        <Form
          form={passwordForm}
          onFinish={onPasswordFinish}
          onFinishFailed={onFinishFailed}
          scrollToFirstError
          size="large"
        >
          <Title level={3} className="UserProfile-margin-top">
            Update Password
          </Title>
          <Title level={4}>Current Password:</Title>
          <Form.Item
            name="currentPassword"
            rules={[
              {
                required: true,
                message: 'Please enter your current password!',
              },
            ]}
          >
            <Password placeholder="Current Password" />
          </Form.Item>
          <Title level={4}>New Password:</Title>
          <Form.Item
            name="newPassword"
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please enter a value for password!',
              },
              () => ({
                validator(rule, value) {
                  if (
                    !value ||
                    value.match(
                      `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[()&.,@?#$/!%+]).{8,}`,
                    )
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(`Password must contain at least 8 characters, one uppercase and one
                    lowercase character, one number, and one special character - () & .
                    , @ ? # $ / ! % +`),
                  );
                },
              }),
            ]}
          >
            <Password placeholder="New Password" />
          </Form.Item>
          <Title level={4}>Confirm New Password:</Title>
          <Form.Item
            name="confirmNewPassword"
            dependencies={['newPassword']}
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please confirm your password!',
              },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do no match!'));
                },
              }),
            ]}
          >
            <Password placeholder="Confirm New Password" />
          </Form.Item>
          <Row gutter={16}>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting === 'PASSWORD'}
              >
                Save
              </Button>
            </Col>
            <Col>
              <Button onClick={onCancelClick}>Cancel</Button>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

export default AccountSettings;
